import { DocumentService, UserService } from "@/service/";
import type { TDocument, TRegisterForm, TUser } from "@/types";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

type TUserStore = {
   workspaceCurrentUserMap: Record<string, TUser>;
   workspaceUsersMap: Record<string, Record<string, TUser>>;
   setCurrentUser: (slug: string, user: TUser) => void;
   setWorkspaceUsers: (slug: string, users: TUser[]) => void;
   updateWorkspaceUser: (
      slug: string,
      userId: string,
      user: Partial<TUser>,
   ) => void;
   fetchCurrentUser: (slug: string) => Promise<TUser>;
   fetchWorkspaceUsers: (slug: string) => Promise<TUser[]>;
   createUser: (slug: string, payload: TRegisterForm) => Promise<TUser>;
   updateUser: (
      slug: string,
      userId: string,
      user: Partial<TUser & { removeImage: boolean }> | { photo: File },
   ) => Promise<void>;
   addDocument(
      slug: string,
      payload: Pick<TDocument, "userId" | "type" | "images" | "number">,
   ): Promise<TDocument>;
   deleteDocument(slug: string, userId: string, id: number): Promise<void>;
};
const userService = new UserService();
const documentService = new DocumentService();

export const useUserStore = create<TUserStore>()(
   immer((set) => ({
      workspaceCurrentUserMap: {},
      workspaceUsersMap: {},

      setCurrentUser: (slug, user) => {
         set((state: TUserStore) => {
            state.workspaceCurrentUserMap[slug] = user;
         });
      },

      setWorkspaceUsers: (slug, users) => {
         set((state: TUserStore) => {
            state.workspaceUsersMap[slug] ??= {};
            for (const u of users) {
               state.workspaceUsersMap[slug][u.id] = u;
            }
         });
      },

      updateWorkspaceUser: (slug, userId, user) => {
         set((state: TUserStore) => {
            if (state.workspaceCurrentUserMap[slug]?.id === userId) {
               state.workspaceCurrentUserMap[slug] = {
                  ...state.workspaceCurrentUserMap[slug],
                  ...user,
               };
            }
            const existingUser = state.workspaceUsersMap[slug][userId];
            state.workspaceUsersMap[slug][userId] = {
               ...existingUser,
               ...user,
            };
         });
      },

      fetchCurrentUser: async (slug) => {
         return userService.getCurrentUser(slug).then((user) => {
            set((state: TUserStore) => {
               state.workspaceCurrentUserMap[slug] = user;
            });
            return user;
         });
      },

      fetchWorkspaceUsers: async (slug: string) => {
         return userService.getUsers(slug).then((users) => {
            set((state: TUserStore) => {
               state.workspaceUsersMap[slug] ??= {};
               for (const u of users) {
                  state.workspaceUsersMap[slug][u.id] = u;
               }
            });
            return users;
         });
      },

      createUser: async (slug, payload) => {
         return userService.createUser(slug, payload).then((user) => {
            set((state: TUserStore) => {
               state.workspaceUsersMap[slug] ??= {};
               state.workspaceUsersMap[slug][user.id] = user;
            });
            return user;
         });
      },

      updateUser: async (
         slug: string,
         userId: string,
         user: Partial<TUser> | { photo: File },
      ) => {
         return userService.updateUser(slug, userId, user).then((response) => {
            set((state: TUserStore) => {
               state.workspaceUsersMap[slug][userId] = response;
            });
         });
      },

      addDocument: async (slug, payload) => {
         return documentService.addDocument(slug, payload).then((document) => {
            set((store) => {
               const user = store.workspaceUsersMap[slug]?.[payload.userId];
               if (user) {
                  user.documents.push(document);
               }
            });
            return document;
         });
      },

      deleteDocument: async (slug, userId, id) => {
         return documentService.deleteDocument(slug, id).then(() => {
            set((store) => {
               const user = store.workspaceUsersMap[slug]?.[userId];
               if (user) {
                  user.documents = user.documents.filter(
                     (document) => document.id !== id,
                  );
               }
            });
         });
      },
   })),
);
